import React, { useEffect, useState } from 'react';
import { useSalesGoalsStore } from '../../../../store/salesGoalsStore';
import { SalesGoal } from '../../../../services/salesGoalsApi';
import styles from './ManageGoalsModal.module.css';

interface ManageGoalsModalProps {
  open: boolean;
  onClose: () => void;
}

const toDateInput = (iso: string) => {
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ''; }
};

export const ManageGoalsModal: React.FC<ManageGoalsModalProps> = ({ open, onClose }) => {
  const goals = useSalesGoalsStore((s) => s.goals);
  const fetchGoals = useSalesGoalsStore((s) => s.fetchGoals);
  const upsert = useSalesGoalsStore((s) => s.upsert);
  const remove = useSalesGoalsStore((s) => s.remove);

  const [salesRepId, setSalesRepId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [targetRevenue, setTargetRevenue] = useState('');
  const [targetOrders, setTargetOrders] = useState('');
  const [targetCustomers, setTargetCustomers] = useState('');
  const [targetVisits, setTargetVisits] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      fetchGoals().catch(() => undefined);
      resetForm();
    }
  }, [open, fetchGoals]);

  if (!open) return null;

  const resetForm = () => {
    setSalesRepId('');
    setPeriodStart('');
    setPeriodEnd('');
    setTargetRevenue('');
    setTargetOrders('');
    setTargetCustomers('');
    setTargetVisits('');
    setError(null);
    setEditingId(null);
  };

  const loadIntoForm = (g: SalesGoal) => {
    setSalesRepId(String(g.sales_rep_id));
    setPeriodStart(toDateInput(g.period_start));
    setPeriodEnd(toDateInput(g.period_end));
    setTargetRevenue(String(g.target_revenue));
    setTargetOrders(String(g.target_orders));
    setTargetCustomers(String(g.target_new_customers));
    setTargetVisits(String(g.target_visits));
    setEditingId(g.id);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!salesRepId || !periodStart || !periodEnd) {
      setError('Sales Rep ID, start and end dates are required');
      return;
    }
    if (new Date(periodStart) > new Date(periodEnd)) {
      setError('Start date must be on or before end date');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await upsert({
        sales_rep_id: Number(salesRepId),
        period_start: periodStart,
        period_end: periodEnd,
        target_revenue: targetRevenue ? Number(targetRevenue) : undefined,
        target_orders: targetOrders ? Number(targetOrders) : undefined,
        target_new_customers: targetCustomers ? Number(targetCustomers) : undefined,
        target_visits: targetVisits ? Number(targetVisits) : undefined,
      });
      resetForm();
      fetchGoals().catch(() => undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save goal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (g: SalesGoal) => {
    if (!window.confirm(`Delete goal for rep #${g.sales_rep_id} (${toDateInput(g.period_start)} → ${toDateInput(g.period_end)})?`)) return;
    try {
      await remove(g.id);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to delete goal');
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Manage Sales Goals</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className={styles.body}>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>{editingId ? 'Edit Goal' : 'Set / Upsert Goal'}</h3>
            <p className={styles.note}>
              Upserts by (sales rep, period). Re-using an existing rep + period updates the row.
              Period must be a unique date range per rep.
            </p>
            <form onSubmit={handleSubmit} className={styles.form}>
              {error && <p className={styles.error}>{error}</p>}

              <div className={styles.row}>
                <label className={styles.field}>
                  <span>Sales Rep ID <span className={styles.required}>*</span></span>
                  <input type="number" min="1" value={salesRepId} onChange={(e) => setSalesRepId(e.target.value)} required />
                </label>
                <label className={styles.field}>
                  <span>&nbsp;</span>
                  <button type="button" className={styles.linkBtn} onClick={resetForm}>
                    Reset form
                  </button>
                </label>
              </div>

              <div className={styles.row}>
                <label className={styles.field}>
                  <span>Period Start <span className={styles.required}>*</span></span>
                  <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} required />
                </label>
                <label className={styles.field}>
                  <span>Period End <span className={styles.required}>*</span></span>
                  <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} required />
                </label>
              </div>

              <div className={styles.row}>
                <label className={styles.field}>
                  <span>Target Revenue ($)</span>
                  <input type="number" min="0" step="0.01" value={targetRevenue} onChange={(e) => setTargetRevenue(e.target.value)} />
                </label>
                <label className={styles.field}>
                  <span>Target Orders</span>
                  <input type="number" min="0" value={targetOrders} onChange={(e) => setTargetOrders(e.target.value)} />
                </label>
              </div>

              <div className={styles.row}>
                <label className={styles.field}>
                  <span>Target New Customers</span>
                  <input type="number" min="0" value={targetCustomers} onChange={(e) => setTargetCustomers(e.target.value)} />
                </label>
                <label className={styles.field}>
                  <span>Target Visits</span>
                  <input type="number" min="0" value={targetVisits} onChange={(e) => setTargetVisits(e.target.value)} />
                </label>
              </div>

              <div className={styles.actions}>
                <button type="submit" className={styles.submit} disabled={submitting}>
                  {submitting ? 'Saving…' : editingId ? 'Update Goal' : 'Save Goal'}
                </button>
              </div>
            </form>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Existing Goals</h3>
            {goals.length === 0 ? (
              <p className={styles.empty}>No goals yet.</p>
            ) : (
              <div className={styles.goalsList}>
                {goals.map((g) => (
                  <div key={g.id} className={styles.goalRow}>
                    <div className={styles.goalInfo}>
                      <strong>{g.sales_rep_name || `Rep #${g.sales_rep_id}`}</strong>
                      <span>{toDateInput(g.period_start)} → {toDateInput(g.period_end)}</span>
                      <span>Revenue: ${g.target_revenue.toLocaleString()} · Orders: {g.target_orders}</span>
                    </div>
                    <div className={styles.goalActions}>
                      <button type="button" className={styles.editLink} onClick={() => loadIntoForm(g)}>Edit</button>
                      <button type="button" className={styles.deleteLink} onClick={() => handleDelete(g)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ManageGoalsModal;
